/**
 * HLS to MP4 Converter Utility
 * 
 * This utility provides functionality to convert HLS streams to MP4 files
 * that can be downloaded directly in the browser using MediaRecorder API.
 * 
 * Enhanced with:
 * - Better error handling for video loading
 * - CORS handling for Mux streams
 * - Timeout handling for slow-loading videos
 * - URL accessibility checks
 * - Improved fallback behavior
 */

// Dynamic import for hls.js to handle cases where the package might not be installed
const loadHlsJs = async () => {
  try {
    console.log('🔍 [HLS Debug] Attempting to load hls.js from npm package...');
    const Hls = await import('hls.js');
    console.log('🔍 [HLS Debug] hls.js loaded from npm package successfully');
    console.log('🔍 [HLS Debug] Hls.default:', Hls.default);
    return Hls.default;
  } catch (error) {
    console.warn('🔍 [HLS Debug] hls.js not available from npm, trying CDN fallback...');
    console.warn('🔍 [HLS Debug] Import error:', error.message);
    
    // Fallback to CDN version if the npm package is not available
    return new Promise((resolve, reject) => {
      if (window.Hls) {
        console.log('🔍 [HLS Debug] hls.js already available on window object');
        resolve(window.Hls);
        return;
      }
      
      console.log('🔍 [HLS Debug] Loading hls.js from CDN...');
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
      script.onload = () => {
        console.log('🔍 [HLS Debug] hls.js loaded from CDN successfully');
        console.log('🔍 [HLS Debug] window.Hls:', window.Hls);
        resolve(window.Hls);
      };
      script.onerror = () => {
        console.error('🔍 [HLS Debug] Failed to load hls.js from CDN');
        reject(new Error('Failed to load hls.js from CDN'));
      };
      document.head.appendChild(script);
    });
  }
};

// Dynamic import for RecordRTC to handle cases where the package might not be installed
const loadRecordRTC = async () => {
  try {
    const RecordRTC = await import('recordrtc');
    return RecordRTC.default;
  } catch (error) {
    console.warn('recordrtc not available, trying CDN fallback');
    // Fallback to CDN version if the npm package is not available
    return new Promise((resolve, reject) => {
      if (window.RecordRTC) {
        resolve(window.RecordRTC);
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://cdn.webrtc-experiment.com/RecordRTC.js';
      script.onload = () => resolve(window.RecordRTC);
      script.onerror = () => reject(new Error('Failed to load RecordRTC from CDN'));
      document.head.appendChild(script);
    });
  }
};

/**
 * Check if the browser supports the required APIs for HLS to MP4 conversion
 * @param {boolean} detailed - If true, returns detailed capabilities object
 * @returns {boolean|object} - Boolean support status or detailed capabilities
 */
export const isHlsToMp4Supported = (detailed = false) => {
  console.log('🔍 [HLS Debug] Checking browser capabilities for HLS to MP4 conversion...');
  
  const hasMediaRecorder = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported;
  const hasCanvas = typeof HTMLCanvasElement !== 'undefined';
  const hasWebGL = (() => {
    try {
      const canvas = document.createElement('canvas');
      return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
    } catch (e) {
      return false;
    }
  })();

  // Test HLS playback support
  const hasNativeHls = (() => {
    const video = document.createElement('video');
    return video.canPlayType('application/vnd.apple.mpegurl') !== '';
  })();

  const hasHlsJs = (() => {
    try {
      // Check if hls.js is available globally
      return typeof window !== 'undefined' && (window.Hls || false);
    } catch (e) {
      return false;
    }
  })();

  // Get supported MIME types for recording
  const supportedMimeTypes = [];
  const testTypes = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus', 
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4;codecs=h264,aac',
    'video/mp4'
  ];

  if (hasMediaRecorder) {
    testTypes.forEach(type => {
      if (MediaRecorder.isTypeSupported(type)) {
        supportedMimeTypes.push(type);
      }
    });
  }

  const capabilities = {
    mediaRecorder: hasMediaRecorder,
    canvas: hasCanvas,
    webgl: hasWebGL,
    nativeHls: hasNativeHls,
    hlsJs: hasHlsJs,
    hlsPlayback: hasNativeHls || hasHlsJs,
    supportedMimeTypes,
    overall: hasMediaRecorder && hasCanvas && hasWebGL && (hasNativeHls || hasHlsJs)
  };

  console.log('🔍 [HLS Debug] Browser capabilities:', capabilities);

  if (detailed) {
    return capabilities;
  }

  return capabilities.overall;
};

/**
 * Check if an HLS URL is accessible before attempting conversion
 * @param {string} hlsUrl - The HLS stream URL to check
 * @param {number} timeout - Timeout in milliseconds (default: 10000)
 * @returns {Promise<object>} - Accessibility information object
 */
export const checkHlsUrlAccessibility = async (hlsUrl, timeout = 10000) => {
  console.log('🔍 [HLS Debug] Checking URL accessibility:', hlsUrl);
  
  const result = {
    accessible: false,
    isMuxStream: false,
    error: null,
    statusCode: null
  };

  try {
    // Check if it's a Mux stream
    result.isMuxStream = hlsUrl.includes('stream.mux.com') || hlsUrl.includes('.mux.com');
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    const response = await fetch(hlsUrl, {
      method: 'HEAD',
      mode: 'cors',
      signal: controller.signal,
      headers: {
        'Range': 'bytes=0-0' // Minimal request
      }
    });
    
    clearTimeout(timeoutId);
    result.statusCode = response.status;
    result.accessible = response.ok || response.status === 206; // 206 = Partial Content is also OK
    
    console.log('🔍 [HLS Debug] URL accessibility check result:', result);
    return result;
    
  } catch (error) {
    console.warn('🔍 [HLS Debug] URL accessibility check failed:', error.message);
    result.error = error.message;
    // Return accessible=true to allow the attempt - sometimes CORS blocks HEAD requests but allows video loading
    result.accessible = true;
    console.log('🔍 [HLS Debug] Allowing attempt despite accessibility check failure:', result);
    return result;
  }
};

/**
 * Detect HLS support in the browser
 * @returns {object} - HLS support information
 */
export const detectHlsSupport = () => {
  console.log('🔍 [HLS Debug] Detecting HLS support...');
  
  const result = {
    hasNativeSupport: false,
    needsHlsJs: false,
    canUseConverter: false,
    hlsJsAvailable: false
  };

  // Check native HLS support (Safari)
  const video = document.createElement('video');
  result.hasNativeSupport = video.canPlayType('application/vnd.apple.mpegurl') !== '';

  // Check if hls.js is available
  try {
    result.hlsJsAvailable = typeof window !== 'undefined' && (window.Hls || false);
  } catch (e) {
    result.hlsJsAvailable = false;
  }

  // Determine if we need hls.js (Chrome, Firefox, etc.)
  result.needsHlsJs = !result.hasNativeSupport && result.hlsJsAvailable;

  // Can use converter if we have either native support or hls.js
  result.canUseConverter = result.hasNativeSupport || result.needsHlsJs;

  console.log('🔍 [HLS Debug] HLS support detection result:', result);
  
  return result;
};

/**
 * Get detailed error message from video error event
 * @param {Event} error - The video error event
 * @returns {string} - Detailed error message
 */
const getVideoErrorMessage = (error) => {
  if (!error) return 'Unknown video error';
  
  // Handle MediaError objects
  if (error.target && error.target.error) {
    const mediaError = error.target.error;
    switch (mediaError.code) {
      case MediaError.MEDIA_ERR_ABORTED:
        return 'Video loading was aborted by user';
      case MediaError.MEDIA_ERR_NETWORK:
        return 'Network error occurred while loading video';
      case MediaError.MEDIA_ERR_DECODE:
        return 'Video decoding error - format may be unsupported';
      case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
        return 'Video format or source not supported';
      default:
        return `Video error (code: ${mediaError.code}): ${mediaError.message || 'Unknown error'}`;
    }
  }
  
  // Handle regular error objects
  if (error.message) {
    return `Video loading error: ${error.message}`;
  }
  
  // Handle error events
  if (error.type) {
    return `Video ${error.type} error occurred`;
  }
  
  return 'Unknown video loading error occurred';
};

/**
 * Convert HLS stream to MP4 and trigger download
 * 
 * @param {string} hlsUrl - The HLS stream URL (.m3u8)
 * @param {string} filename - Desired filename for the downloaded MP4
 * @param {function} onProgress - Callback for progress updates (0-100)
 * @param {function} onError - Callback for error handling
 * @param {function} onComplete - Callback when conversion is complete
 * @returns {function} - Function to stop the recording
 */
export const convertHlsToMp4 = async (
  hlsUrl,
  filename = 'video.mp4',
  onProgress = () => {},
  onError = () => {},
  onComplete = () => {}
) => {
  let recorder = null;
  let video = null;
  let hls = null;
  let isRecording = false;

  try {
    console.log('🔍 [HLS Debug] Starting HLS to MP4 conversion for URL:', hlsUrl);
    
    // Check browser support (but attempt anyway)
    console.log('🔍 [HLS Debug] Checking browser support...');
    const browserSupport = isHlsToMp4Supported();
    if (!browserSupport) {
      console.warn('🔍 [HLS Debug] Initial browser support check failed, but attempting conversion anyway');
    } else {
      console.log('🔍 [HLS Debug] Browser support confirmed ✅');
    }

    // Load required libraries
    console.log('🔍 [HLS Debug] Loading required libraries...');
    console.log('🔍 [HLS Debug] Loading hls.js...');
    const hlsLoadStart = performance.now();
    const Hls = await loadHlsJs();
    const hlsLoadTime = performance.now() - hlsLoadStart;
    console.log(`🔍 [HLS Debug] hls.js loaded successfully in ${hlsLoadTime.toFixed(2)}ms ✅`);
    console.log('🔍 [HLS Debug] hls.js object:', Hls);
    console.log('🔍 [HLS Debug] hls.js isSupported:', Hls.isSupported ? Hls.isSupported() : 'isSupported method not available');
    
    console.log('🔍 [HLS Debug] Loading RecordRTC...');
    const recordRtcLoadStart = performance.now();
    const RecordRTC = await loadRecordRTC();
    const recordRtcLoadTime = performance.now() - recordRtcLoadStart;
    console.log(`🔍 [HLS Debug] RecordRTC loaded successfully in ${recordRtcLoadTime.toFixed(2)}ms ✅`);

    // Check URL accessibility first
    console.log('🔍 [HLS Debug] Checking URL accessibility...');
    const accessibilityResult = await checkHlsUrlAccessibility(hlsUrl);
    if (!accessibilityResult.accessible) {
      console.warn('🔍 [HLS Debug] HLS URL may not be accessible, but proceeding with conversion attempt');
    } else {
      console.log('🔍 [HLS Debug] URL accessibility confirmed ✅');
    }

    // Create hidden video element with better CORS handling
    console.log('🔍 [HLS Debug] Creating video element...');
    video = document.createElement('video');
    video.style.position = 'absolute';
    video.style.left = '-9999px';
    video.style.width = '640px';
    video.style.height = '360px';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous'; // Enable CORS for Mux streams
    video.preload = 'metadata';
    document.body.appendChild(video);
    console.log('🔍 [HLS Debug] Video element created and appended to body ✅');

    // Add timeout for video loading
    const VIDEO_LOAD_TIMEOUT = 30000; // 30 seconds
    let loadTimeout;
    
    const setupLoadTimeout = () => {
      loadTimeout = setTimeout(() => {
        onError(new Error('Video loading timeout - the HLS stream took too long to load. This may be due to network issues or the stream being unavailable.'));
        cleanup();
      }, VIDEO_LOAD_TIMEOUT);
    };
    
    const clearLoadTimeout = () => {
      if (loadTimeout) {
        clearTimeout(loadTimeout);
        loadTimeout = null;
      }
    };

    // Setup HLS
    console.log('🔍 [HLS Debug] Setting up HLS...');
    console.log('🔍 [HLS Debug] Checking Hls.isSupported()...');
    const hlsSupported = Hls.isSupported();
    console.log('🔍 [HLS Debug] Hls.isSupported() result:', hlsSupported);
    
    if (hlsSupported) {
      console.log('🔍 [HLS Debug] Creating new Hls instance...');
      hls = new Hls({
        enableWorker: false,
        lowLatencyMode: true,
        backBufferLength: 90
      });
      console.log('🔍 [HLS Debug] Hls instance created:', hls);
      
      console.log('🔍 [HLS Debug] Loading HLS source:', hlsUrl);
      hls.loadSource(hlsUrl);
      console.log('🔍 [HLS Debug] HLS source loaded ✅');
      
      console.log('🔍 [HLS Debug] Attaching HLS to video element...');
      hls.attachMedia(video);
      console.log('🔍 [HLS Debug] HLS attached to video element ✅');
      
      console.log('🔍 [HLS Debug] Setting up HLS event listeners...');
      hls.on(Hls.Events.MANIFEST_PARSED, async () => {
        try {
          clearLoadTimeout();
          console.log('🔍 [HLS Debug] HLS manifest parsed successfully ✅');
          console.log('🔍 [HLS Debug] Video duration:', video.duration);
          console.log('🔍 [HLS Debug] Video dimensions:', video.videoWidth, 'x', video.videoHeight);
          console.log('🔍 [HLS Debug] Attempting to play video...');
          await video.play();
          console.log('🔍 [HLS Debug] Video play started ✅');
          startRecording();
        } catch (err) {
          clearLoadTimeout();
          console.error('🔍 [HLS Debug] Failed to start video playback:', err);
          onError(new Error('Failed to start video playbook: ' + err.message));
        }
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        clearLoadTimeout();
        console.error('🔍 [HLS Debug] HLS Error event received:', event);
        console.error('🔍 [HLS Debug] HLS Error data:', data);
        console.error('🔍 [HLS Debug] Error details:', {
          fatal: data.fatal,
          type: data.type,
          details: data.details,
          reason: data.reason,
          response: data.response
        });
        
        if (data.fatal) {
          let errorMessage = 'HLS streaming error occurred';
          
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              errorMessage = 'Network error while loading HLS stream. Check your internet connection and ensure the stream URL is accessible.';
              console.error('🔍 [HLS Debug] Fatal NETWORK_ERROR detected');
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              errorMessage = 'Media decoding error with HLS stream. The video format may not be supported by your browser.';
              console.error('🔍 [HLS Debug] Fatal MEDIA_ERROR detected');
              break;
            case Hls.ErrorTypes.KEY_SYSTEM_ERROR:
              errorMessage = 'DRM or encryption error with HLS stream.';
              console.error('🔍 [HLS Debug] Fatal KEY_SYSTEM_ERROR detected');
              break;
            case Hls.ErrorTypes.MUX_ERROR:
              errorMessage = 'Multiplexing error with HLS stream.';
              console.error('🔍 [HLS Debug] Fatal MUX_ERROR detected');
              break;
            default:
              errorMessage = `HLS Error (${data.type}): ${data.details || 'Unknown error'}`;
              console.error('🔍 [HLS Debug] Fatal error of unknown type:', data.type);
          }
          
          console.error('🔍 [HLS Debug] Calling onError with message:', errorMessage);
          onError(new Error(errorMessage));
        } else {
          console.warn('🔍 [HLS Debug] Non-fatal HLS error, continuing...');
        }
      });
      
      setupLoadTimeout();

    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      console.log('🔍 [HLS Debug] Using native HLS support (Safari)');
      console.log('🔍 [HLS Debug] Setting video.src to:', hlsUrl);
      video.src = hlsUrl;
      
      video.addEventListener('loadedmetadata', async () => {
        try {
          clearLoadTimeout();
          console.log('Native HLS loaded successfully');
          await video.play();
          startRecording();
        } catch (err) {
          clearLoadTimeout();
          onError(new Error('Failed to start video playback: ' + err.message));
        }
      });
      
      video.addEventListener('error', (error) => {
        clearLoadTimeout();
        const errorMessage = getVideoErrorMessage(error);
        onError(new Error(errorMessage));
      });
      
      setupLoadTimeout();
      
    } else {
      throw new Error('HLS is not supported in this browser. Please use a modern browser that supports HLS streaming.');
    }

    const startRecording = () => {
      try {
        // Get video stream
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 360;

        // Create MediaStream from canvas
        const stream = canvas.captureStream(30); // 30 FPS
        
        // Add audio track if available
        if (video.captureStream) {
          const videoStream = video.captureStream();
          const audioTracks = videoStream.getAudioTracks();
          audioTracks.forEach(track => stream.addTrack(track));
        }

        // Setup canvas drawing loop
        const drawVideoFrame = () => {
          if (isRecording && !video.paused && !video.ended) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            requestAnimationFrame(drawVideoFrame);
          }
        };

        // Start recording
        const options = {
          type: 'video',
          mimeType: 'video/webm;codecs=vp9',
          videoBitsPerSecond: 2500000,
          audioBitsPerSecond: 128000,
          frameInterval: 10
        };

        // Fallback mimeType if VP9 is not supported
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = 'video/webm;codecs=vp8';
          if (!MediaRecorder.isTypeSupported(options.mimeType)) {
            options.mimeType = 'video/webm';
          }
        }

        recorder = new RecordRTC(stream, options);
        recorder.startRecording();
        isRecording = true;

        // Start drawing video frames
        drawVideoFrame();

        // Progress tracking
        let progressInterval = setInterval(() => {
          if (video.duration && video.currentTime) {
            const progress = Math.min((video.currentTime / video.duration) * 100, 100);
            onProgress(progress);
            
            if (video.ended) {
              stopRecording();
              clearInterval(progressInterval);
            }
          }
        }, 1000);

        onProgress(0);

      } catch (err) {
        onError(new Error('Failed to start recording: ' + err.message));
      }
    };

    const stopRecording = () => {
      isRecording = false;
      
      if (recorder) {
        recorder.stopRecording(() => {
          const blob = recorder.getBlob();
          
          // Create download link
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename.endsWith('.mp4') ? filename : filename + '.mp4';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          // Cleanup
          cleanup();
          onComplete();
        });
      }
    };

    const cleanup = () => {
      // Clear any timeouts
      clearLoadTimeout();
      
      // Cleanup video element
      if (video) {
        try {
          video.pause();
          video.src = '';
          video.load(); // Reset video element
          if (document.body.contains(video)) {
            document.body.removeChild(video);
          }
        } catch (e) {
          console.warn('Error cleaning up video element:', e);
        }
      }
      
      // Cleanup HLS instance
      if (hls) {
        try {
          hls.destroy();
        } catch (e) {
          console.warn('Error cleaning up HLS instance:', e);
        }
      }
      
      // Cleanup recorder
      if (recorder) {
        try {
          recorder.destroy();
        } catch (e) {
          console.warn('Error cleaning up recorder:', e);
        }
      }
    };

    // Return stop function for manual stopping
    return {
      stop: stopRecording,
      cleanup: cleanup
    };

  } catch (error) {
    console.error('HLS to MP4 conversion error:', error);
    
    // Enhanced error message for common issues
    let enhancedMessage = error.message;
    if (error.message.includes('CORS')) {
      enhancedMessage += ' This may be due to Cross-Origin Resource Sharing (CORS) restrictions. Try using a CORS proxy or ensure the video source allows cross-origin requests.';
    } else if (error.message.includes('network')) {
      enhancedMessage += ' Please check your internet connection and verify that the HLS stream URL is accessible.';
    } else if (error.message.includes('timeout')) {
      enhancedMessage += ' The video stream is taking too long to load. Try again or check if the stream is available.';
    }
    
    onError(new Error(enhancedMessage));
    
    // Cleanup is not available in catch block, return empty functions
    
    return { 
      stop: () => {}, 
      cleanup: () => {} 
    };
  }
};

/**
 * Alternative method using native browser APIs without external libraries
 * This is a simpler approach that works with browsers supporting MediaRecorder
 */
export const convertHlsToMp4Simple = async (
  hlsUrl,
  filename = 'video.mp4',
  onProgress = () => {},
  onError = () => {},
  onComplete = () => {}
) => {
  try {
    // Check if browser supports MediaRecorder
    if (!window.MediaRecorder) {
      throw new Error('MediaRecorder API is not supported in this browser');
    }

    // Check URL accessibility first
    const isAccessible = await checkHlsUrlAccessibility(hlsUrl, 5000);
    if (!isAccessible) {
      console.warn('HLS URL may not be accessible, but proceeding with simple conversion attempt');
    }

    // Create video element with enhanced CORS and error handling
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.style.position = 'absolute';
    video.style.left = '-9999px';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    document.body.appendChild(video);

    // Setup timeout for video loading
    const SIMPLE_LOAD_TIMEOUT = 20000; // 20 seconds for simple method
    let loadTimeout;
    
    // Load HLS stream
    video.src = hlsUrl;

    return new Promise((resolve, reject) => {
      loadTimeout = setTimeout(() => {
        reject(new Error('Video loading timeout in simple converter - the HLS stream took too long to load'));
      }, SIMPLE_LOAD_TIMEOUT);
      
      video.addEventListener('loadedmetadata', async () => {
        try {
          clearTimeout(loadTimeout);
          
          // Enhanced HLS support check
          const hlsSupport = detectHlsSupport();
          if (!hlsSupport.canUseConverter) {
            const capabilities = isHlsToMp4Supported(true);
            let errorMsg = 'HLS playback not supported in this browser.';
            
            if (!hlsSupport.hasNativeSupport && !hlsSupport.needsHlsJs) {
              errorMsg += ' Neither native HLS support nor hls.js compatibility detected.';
            }
            if (!capabilities.mediaRecorder) {
              errorMsg += ' MediaRecorder API not available.';
            }
            
            const error = new Error(errorMsg + ' Please try opening the stream directly.');
            error.fallbackAvailable = true;
            error.fallbackAction = () => {
              window.open(hlsUrl, '_blank', 'noopener,noreferrer');
            };
            throw error;
          }

          console.log('Video metadata loaded for simple conversion');
          await video.play();

          // Enhanced stream capture with better compatibility
          let stream;
          if (video.captureStream) {
            stream = video.captureStream(30); // 30 FPS
          } else if (video.mozCaptureStream) {
            stream = video.mozCaptureStream(30);
          } else {
            // Check if this is Safari and provide more specific message
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;
            const isSafariBrowser = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
            
            let errorMessage = 'Video capture stream not supported in this browser';
            if (isSafariBrowser) {
              errorMessage = 'Safari detected: video.captureStream() API not supported. Safari has native HLS support for playback, but does not support programmatic video capture for downloads.';
            }
            
            const error = new Error(errorMessage);
            error.fallbackAvailable = true;
            error.fallbackAction = () => {
              window.open(hlsUrl, '_blank', 'noopener,noreferrer');
            };
            throw error;
          }

          // Get supported mime types and select the best one
          const capabilities = isHlsToMp4Supported(true);
          const supportedTypes = capabilities.supportedMimeTypes;
          
          if (supportedTypes.length === 0) {
            const error = new Error('No supported recording formats available');
            error.fallbackAvailable = true;
            error.fallbackAction = () => {
              window.open(hlsUrl, '_blank', 'noopener,noreferrer');
            };
            throw error;
          }

          const mediaRecorder = new MediaRecorder(stream, {
            mimeType: supportedTypes[0]
          });
          
          console.log(`Simple converter using: ${supportedTypes[0]}`);

          const chunks = [];

          mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              chunks.push(event.data);
            }
          };

          mediaRecorder.onstop = () => {
            clearTimeout(loadTimeout);
            
            if (chunks.length === 0) {
              const error = new Error('No video data was recorded. The stream may be empty or incompatible.');
              error.fallbackAvailable = true;
              error.fallbackAction = () => {
                window.open(hlsUrl, '_blank', 'noopener,noreferrer');
              };
              onError(error);
              reject(error);
              return;
            }
            
            const blob = new Blob(chunks, { type: mediaRecorder.mimeType });
            
            if (blob.size === 0) {
              const error = new Error('Recorded video file is empty. The conversion may have failed.');
              error.fallbackAvailable = true;
              error.fallbackAction = () => {
                window.open(hlsUrl, '_blank', 'noopener,noreferrer');
              };
              onError(error);
              reject(error);
              return;
            }
            
            try {
              // Create download with proper file extension
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              
              // Determine proper file extension based on mimeType
              let extension = '.webm';
              if (mediaRecorder.mimeType.includes('mp4')) {
                extension = '.mp4';
              } else if (mediaRecorder.mimeType.includes('webm')) {
                extension = '.webm';
              }
              
              a.download = filename.replace(/\.[^/.]+$/, '') + extension;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
              
              console.log(`Successfully converted and downloaded video: ${a.download} (${(blob.size / 1024 / 1024).toFixed(2)} MB)`);
            } catch (downloadError) {
              console.error('Error during download:', downloadError);
              const error = new Error('Failed to download converted video: ' + downloadError.message);
              error.fallbackAvailable = true;
              error.fallbackAction = () => {
                window.open(hlsUrl, '_blank', 'noopener,noreferrer');
              };
              onError(error);
              reject(downloadError);
              return;
            }

            // Cleanup
            try {
              video.pause();
              if (document.body.contains(video)) {
                document.body.removeChild(video);
              }
            } catch (cleanupError) {
              console.warn('Error during final cleanup:', cleanupError);
            }
            
            onComplete();
            resolve();
          };

          mediaRecorder.onerror = (event) => {
            onError(new Error('MediaRecorder error: ' + event.error));
            reject(event.error);
          };

          // Start recording
          mediaRecorder.start();
          onProgress(0);

          // Progress tracking
          const progressInterval = setInterval(() => {
            if (video.duration && video.currentTime) {
              const progress = Math.min((video.currentTime / video.duration) * 100, 100);
              onProgress(progress);
              
              if (video.ended) {
                mediaRecorder.stop();
                clearInterval(progressInterval);
              }
            }
          }, 1000);

          // Auto-stop when video ends
          video.addEventListener('ended', () => {
            if (mediaRecorder.state === 'recording') {
              mediaRecorder.stop();
            }
            clearInterval(progressInterval);
          });

        } catch (err) {
          clearTimeout(loadTimeout);
          console.error('Error in simple converter playback setup:', err);
          
          try {
            if (document.body.contains(video)) {
              document.body.removeChild(video);
            }
          } catch (cleanupError) {
            console.warn('Error during video cleanup in catch block:', cleanupError);
          }
          
          onError(err);
          reject(err);
        }
      });

      video.addEventListener('error', (error) => {
        clearTimeout(loadTimeout);
        
        const errorMessage = getVideoErrorMessage(error);
        console.error('Simple converter video error:', errorMessage);
        
        try {
          if (document.body.contains(video)) {
            document.body.removeChild(video);
          }
        } catch (cleanupError) {
          console.warn('Error during video cleanup:', cleanupError);
        }
        
        const enhancedError = new Error(errorMessage + ' You can try opening the stream directly instead.');
        enhancedError.fallbackAvailable = true;
        enhancedError.fallbackAction = () => {
          window.open(hlsUrl, '_blank', 'noopener,noreferrer');
        };
        
        onError(enhancedError);
        reject(enhancedError);
      });
      
      // Add additional error handlers
      video.addEventListener('abort', () => {
        clearTimeout(loadTimeout);
        const error = new Error('Video loading was aborted');
        onError(error);
        reject(error);
      });
      
      video.addEventListener('stalled', () => {
        console.warn('Video loading stalled - network may be slow');
      });
    });

  } catch (error) {
    // Enhanced error handling for simple converter
    if (!error.fallbackAvailable && hlsUrl) {
      error.fallbackAvailable = true;
      error.fallbackAction = () => {
        window.open(hlsUrl, '_blank', 'noopener,noreferrer');
      };
    }
    
    onError(error);
    throw error;
  }
};

/**
 * Utility function to handle fallback when HLS conversion fails
 * @param {Error} error - The error that occurred during conversion 
 * @param {string} hlsUrl - The original HLS URL
 * @param {function} onFallback - Optional callback when fallback is triggered
 * @returns {boolean} - True if fallback was available and triggered
 */
export const handleHlsConversionFallback = (error, hlsUrl, onFallback = null) => {
  if (error.fallbackAvailable && error.fallbackAction) {
    console.log('HLS conversion failed, triggering fallback action');
    
    if (onFallback && typeof onFallback === 'function') {
      onFallback(hlsUrl, error);
    }
    
    error.fallbackAction();
    return true;
  }
  
  // If no specific fallback is available, offer to open in new tab
  if (hlsUrl) {
    console.log('Offering manual fallback to open stream in new tab');
    
    if (onFallback && typeof onFallback === 'function') {
      onFallback(hlsUrl, error);
    }
    
    if (window.confirm('HLS conversion failed. Would you like to open the stream in a new tab instead?')) {
      window.open(hlsUrl, '_blank', 'noopener,noreferrer');
      return true;
    }
  }
  
  return false;
};

/**
 * Test the HLS converter with a Mux stream URL to validate functionality
 * @param {string} muxUrl - The Mux HLS stream URL to test
 * @returns {Promise<object>} - Test results
 */
export const testMuxHlsConverter = async (muxUrl) => {
  const results = {
    urlAccessible: false,
    browserSupported: false,
    hlsSupported: false,
    conversionReady: false,
    recommendations: []
  };

  try {
    // Test URL accessibility
    const accessibilityResult = await checkHlsUrlAccessibility(muxUrl);
    results.urlAccessible = accessibilityResult.accessible;
    
    if (accessibilityResult.isMuxStream) {
      results.recommendations.push('Detected Mux stream - should have good compatibility');
    }
    
    // Test browser capabilities
    const capabilities = isHlsToMp4Supported(true);
    results.browserSupported = capabilities.overall;
    
    if (!capabilities.mediaRecorder) {
      results.recommendations.push('MediaRecorder API not available - try Chrome, Firefox, or Edge');
    }
    
    if (!capabilities.hlsPlayback) {
      results.recommendations.push('HLS playback not supported - ensure hls.js is available');
    }

    // Test HLS support
    const hlsSupport = detectHlsSupport();
    results.hlsSupported = hlsSupport.canUseConverter;
    
    if (hlsSupport.hasNativeSupport) {
      results.recommendations.push('Native HLS support detected (Safari)');
    } else if (hlsSupport.needsHlsJs) {
      results.recommendations.push('hls.js support available');
    }

    // Overall readiness
    results.conversionReady = results.urlAccessible && results.browserSupported && results.hlsSupported;
    
    if (results.conversionReady) {
      results.recommendations.push('Ready for HLS to MP4 conversion!');
    }

  } catch (error) {
    results.recommendations.push(`Test error: ${error.message}`);
  }

  return results;
};

console.log('✅ Enhanced HLS to MP4 Converter with Mux support loaded');